import { useEffect, useRef, useState } from "react";
import { supportApi } from "../../api/support";
import { playNotificationSound } from "../../util";

/**
 * Unread counter behind the "Support Inbox" badge in the sidebar.
 *
 * The badge and the reader of a thread live in different trees - the sidebar is
 * always mounted, the inbox page comes and goes - so reading a thread has to
 * reach the badge somehow. This is a module-level subscriber set rather than a
 * Redux counter because the store is mirrored into localStorage on every single
 * action (see `localStorageMiddleware` in `src/store/index.ts`), and a counter
 * that exists only to say "refetch now" has no business being persisted. It is
 * also the smaller of the two: no slice, no selector, no reducer.
 */
const subscribers = new Set<() => void>();

/**
 * Call after `POST /api/support/conversations/{id}/read` succeeds so the badge
 * drops right away instead of waiting for the next 30s tick.
 */
export const refreshSupportUnread = (): void => {
  subscribers.forEach((notify) => notify());
};

const POLL_INTERVAL_MS = 30000;

interface IUseSupportUnread {
  /** Open conversations waiting on a reply - what the badge shows. */
  conversations: number;
  /** Unread messages across those conversations. */
  messages: number;
}

const EMPTY: IUseSupportUnread = { conversations: 0, messages: 0 };

/**
 * Polls `GET /api/support/unread` every 30s while the tab is visible. A
 * non-staff session never calls it at all: the endpoint answers 404 to
 * everyone else and there is nothing to show them anyway.
 */
export const useSupportUnread = (isStaff: boolean): IUseSupportUnread => {
  const [summary, setSummary] = useState<IUseSupportUnread>(EMPTY);
  const [isTabVisible, setIsTabVisible] = useState<boolean>(
    () => document.visibilityState === "visible"
  );
  // Previous unread-message count, so a rise (a new inbound message) chimes.
  // null until the first fetch establishes the baseline - no chime on load.
  const prevMessagesRef = useRef<number | null>(null);

  useEffect(() => {
    const handleVisibilityChange = (): void => {
      setIsTabVisible(document.visibilityState === "visible");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (!isStaff) {
      setSummary(EMPTY);

      return;
    }

    // A hidden tab arms nothing. Coming back flips isTabVisible, the effect
    // re-runs and the first fetch below is the "refresh once on return".
    if (!isTabVisible) {
      return;
    }

    let isActive = true;

    const fetchSummary = (): void => {
      supportApi
        .getUnreadSummary()
        .then((response) => {
          if (isActive) {
            const prev = prevMessagesRef.current;

            // Chime when unread messages rise, but not on the first fetch.
            if (prev !== null && response.messages > prev) {
              playNotificationSound();
            }
            prevMessagesRef.current = response.messages;

            setSummary({
              conversations: response.conversations,
              messages: response.messages,
            });
          }
        })
        .catch(() => null);
    };

    fetchSummary();
    subscribers.add(fetchSummary);
    const intervalId = window.setInterval(fetchSummary, POLL_INTERVAL_MS);

    return () => {
      isActive = false;
      subscribers.delete(fetchSummary);
      window.clearInterval(intervalId);
    };
  }, [isStaff, isTabVisible]);

  return summary;
};
